import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Chapter,
  Practice,
  PRACTICE_TIME_ALL,
  PRACTICE_TIME_CHAPTER,
  PRACTICE_TIME_RANGE,
  PracticeTime,
  PracticeType,
  Question,
  QuestionRange,
} from "../../models/multiple-question";
import {
  initRoom,
  MAX_QUESTION_TIME_DEFAULT,
  RESULT_TIME_DEFAULT,
  showQuestionsPreview,
} from "../../redux/slices/practiceRoomSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUserSelector } from "../../redux/selector";
import { db } from "../../firebase/config";
import { collection, doc, getDoc, getDocs, query, where, orderBy, limit, startAt, endAt } from "firebase/firestore";
import { toast } from "react-toastify";
import useRedirect from "../../hooks/useRedirect";
import PracticeTimeList from "./PracticeTimeList/practiceTimeList";
import LoadingButton from "../../components/LoadingButton";
import classes from "./practiceReview.module.css";
// @ts-ignore
import mergeClassNames from "merge-class-names";
import LoadingIcon from "../../components/LoadingIcon";
import { MdHistory } from "@react-icons/all-files/md/MdHistory";
import { GoListUnordered } from "@react-icons/all-files/go/GoListUnordered";
import { GoTasklist } from "@react-icons/all-files/go/GoTasklist";
import ControlledSwitch from "../../components/ControlledSwitch";
import { IoList } from "@react-icons/all-files/io5/IoList";
import QuestionPreviewModal from "./QuestionsPreviewModal";
// MotDoiModal (./MotDoiModal/motDoiModal) đã được gỡ khỏi luồng ôn thi để không
// chặn nút Bắt đầu. Code vẫn giữ nguyên trong repo để dùng lại khi cần.
import Tags from "../../components/Tags";
import useHideFooter from "../../hooks/useHideFooter";
import PracticeHome from "../PracticeHome";
import {
  getQuestionsFromStorage,
  saveQuestionsToStorage,
} from "../../service/localStorageService";
import useHideSnowFlakeButton from "../../hooks/useHideSnowFlakeButton";

function PracticeReview() {
  const [practice, setPractice] = useState<Practice | null>(null);
  const [groupByChapter, setGroupByChapter] = useState(false);
  const [numberOfQuestionPerGroup, setNumberOfQuestionPerGroup] = useState<
    number | "OFF"
  >(50);
  const [practiceType, setPracticeType] = useState<PracticeType>("SEQUENCE");
  const [maxQuestionTime, setMaxQuestionTime] = useState<number | "OFF">(
    MAX_QUESTION_TIME_DEFAULT
  );
  const [maxResultTime, setMaxResultTime] = useState<number | "OFF">(
    RESULT_TIME_DEFAULT
  );
  const [showAnswerAfterQuestion, setShowAnswerAfterQuestion] = useState(true);
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [selectedQuestionRange, setSelectedQuestionRange] =
    useState<QuestionRange | null>(null);
  const [initRoomFetching, setInitRoomFetching] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [practiceFetching, setPracticeFetching] = useState(false);
  const [practiceError, setPracticeError] = useState(false);
  const [practiceTimes, setPracticeTimes] = useState<PracticeTime[]>([]);
  const [practiceTimeFetching, setPracticeTimeFetching] = useState(false);
  const [practiceTimeError, setPracticeTimeError] = useState(false);
  const [previewFetching, setPreviewFetching] = useState(false);
  const [suffer, setSuffer] = useState(false);
  const [showNotification, setShowNotification] = useState(true);
  const user = useUserSelector();
  const redirect = useRedirect();
  const navigate = useNavigate();
  useHideFooter();
  useHideSnowFlakeButton();

  useEffect(() => {
    document.title = "Grade PTIT | Ôn thi trắc nghiệm";
  }, []);

  useEffect(() => {
    setSelectedQuestionRange(null);
    setSelectedChapter(null);
    handleRefreshPractice();
    handleRefreshPracticeTime();
  }, [id]);

  const handleRefreshPractice = useCallback(() => {
    if (!id) {
      return;
    }
    setPracticeFetching(true);
    getDoc(doc(collection(db, "practices"), id))
      .then((docSnap) => {
        if (docSnap.exists()) {
          const practiceFetched = docSnap.data() as Practice;
          practiceFetched.id = docSnap.id;
          setPractice(practiceFetched);
        }
        setPracticeError(false);
      })
      .catch(() => {
        setPracticeError(true);
        toast.error("Không thể tải đề luyện tập");
      })
      .finally(() => setPracticeFetching(false));
  }, [id]);

  const handleRefreshPracticeTime = useCallback(() => {
    if (!id || !user) {
      return;
    }
    setPracticeTimeFetching(true);
    const ptQuery = query(
      collection(db, "practice-times"),
      where("practiceId", "==", id),
      where("uid", "==", user.uid),
      orderBy("createAt", "desc"),
      limit(10)
    );
    getDocs(ptQuery)
      .then((snapshot) => {
        return snapshot.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }));
      })
      .then((practiceTimes) => {
        setPracticeTimes(practiceTimes as PracticeTime[]);
        setPracticeTimeError(false);
      })
      .catch((err) => {
        console.error(err);
        setPracticeTimeError(true);
        toast.error("Không thể tải lịch sử luyện tập");
      })
      .finally(() => setPracticeTimeFetching(false));
  }, [id]);

  const getFilterType = () => {
    if (!groupByChapter && selectedQuestionRange) {
      return PRACTICE_TIME_RANGE;
    }
    if (groupByChapter && selectedChapter) {
      return PRACTICE_TIME_CHAPTER;
    }
    return PRACTICE_TIME_ALL;
  };

  const getFilteredQuestions = async () => {
    if (!practice) return;
    const baseConstraints = [
      where("practiceId", "==", practice.id),
      orderBy("idx", "asc"),
    ] as any[];
    let questionQuery;
    if (!groupByChapter && selectedQuestionRange) {
      questionQuery = query(
        collection(db, "questions"),
        ...baseConstraints,
        startAt(selectedQuestionRange.startAt - 1),
        endAt(selectedQuestionRange.endAt - 1)
      );
    } else if (groupByChapter && selectedChapter) {
      questionQuery = query(
        collection(db, "questions"),
        ...baseConstraints,
        where("chapterIdx", "==", selectedChapter.idx)
      );
    } else {
      questionQuery = query(collection(db, "questions"), ...baseConstraints);
    }
    const snapshot = await getDocs(questionQuery);
    return snapshot.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    }));
  };

  const doStartPractice = () => {
    if (!practice) return;
    const type = getFilterType();
    const startRoom = (questions: Question[]) => {
      dispatch(
        initRoom({
          practice,
          practiceDescription: selectedQuestionRange
            ? `Câu hỏi số ${selectedQuestionRange.startAt}-${selectedQuestionRange.endAt}`
            : selectedChapter
            ? selectedChapter.title
            : `Tất cả ${practice.numberOfQuestion} câu hỏi`,
          questions: questions as Question[],
          config: {
            maxResultTime,
            maxQuestionTime,
            showQuestionKeyAfterQuestionEnd: showAnswerAfterQuestion,
            type: type,
            chapterIdx: selectedChapter?.idx,
            questionRange: selectedQuestionRange ?? undefined,
            suffer,
            showNotification,
            practiceType,
          },
        })
      );
      navigate("/practice-control");
    };
    const questionsCached = getQuestionsFromStorage(
      practice.id,
      type,
      selectedQuestionRange,
      selectedChapter?.idx
    );
    if (questionsCached) {
      startRoom(questionsCached);
      return;
    }
    setInitRoomFetching(true);
    getFilteredQuestions()
      .then((questions) => {
        if (!questions || !questions.length) {
          toast.error("Không thể tải danh sách câu hỏi");
        }
        saveQuestionsToStorage(
          practice.id,
          type,
          questions as Question[],
          selectedQuestionRange,
          selectedChapter?.idx
        );
        startRoom(questions as Question[]);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Không thể tải danh sách câu hỏi");
      })
      .finally(() => setInitRoomFetching(false));
  };

  const handleStartControl = () => {
    if (!practice || initRoomFetching) return;
    if (!user) {
      redirect("/login");
      return;
    }
    if (
      !selectedQuestionRange &&
      !selectedChapter &&
      !(!groupByChapter && numberOfQuestionPerGroup === "OFF")
    ) {
      toast.error("Vui lòng chọn chương hoặc bộ câu hỏi");
      return;
    }
    doStartPractice();
  };

  const questionRangeList = useMemo(() => {
    if (!practice || numberOfQuestionPerGroup === "OFF") return [];
    let res: QuestionRange[] = [];
    for (
      let i: number = 0;
      i * numberOfQuestionPerGroup < practice.numberOfQuestion;
      i++
    ) {
      res.push({
        startAt: i * numberOfQuestionPerGroup + 1,
        endAt:
          (i + 1) * numberOfQuestionPerGroup > practice.numberOfQuestion
            ? practice.numberOfQuestion
            : (i + 1) * numberOfQuestionPerGroup,
      });
    }
    return res;
  }, [practice, numberOfQuestionPerGroup]);

  function handleChangeGroupByChapter(value: boolean) {
    setGroupByChapter(value);
    setSelectedChapter(null);
    setSelectedQuestionRange(null);
  }

  function handleChangeNumberOfQuestionPerGroup(value: any) {
    setNumberOfQuestionPerGroup(value);
    setSelectedQuestionRange(null);
  }

  function handleQuestionsPreview() {
    if (initRoomFetching || !practice) return;
    if (
      !selectedQuestionRange &&
      !selectedChapter &&
      !(!groupByChapter && numberOfQuestionPerGroup === "OFF")
    ) {
      toast.error("Vui lòng chọn chương hoặc bộ câu hỏi");
      return;
    }
    const type = getFilterType();
    const questionsCached = getQuestionsFromStorage(
      practice.id,
      type,
      selectedQuestionRange,
      selectedChapter?.idx
    );
    if (questionsCached) {
      dispatch(showQuestionsPreview(questionsCached));
      return;
    }
    setPreviewFetching(true);
    getFilteredQuestions()
      .then((questions) => {
        if (!questions || !questions.length) {
          toast.error("Không thể tải danh sách câu hỏi");
          throw new Error("Không thể tải danh sách câu hỏi");
        }
        saveQuestionsToStorage(
          practice.id,
          type,
          questions as Question[],
          selectedQuestionRange,
          selectedChapter?.idx
        );
        dispatch(showQuestionsPreview(questions as Question[]));
      })
      .catch((err) => console.error(err))
      .finally(() => setPreviewFetching(false));
  }

  if (!id) return <PracticeHome />;

  return (
    <div className={classes.root}>
      <div className={classes.parent}>
        <div className={classes.container}>
          <div className={classes.innerContainer}>
            {practiceFetching ? (
              <div className={classes.emptyWrapper} style={{ height: 100 }}>
                <LoadingIcon />
              </div>
            ) : practiceError ? (
              <div className={classes.emptyWrapper} style={{ height: 100 }}>
                <LoadingButton
                  onClick={handleRefreshPractice}
                  fetching={practiceFetching}
                >
                  Tải lại
                </LoadingButton>
              </div>
            ) : (
              <div className={classes.practice}>
                <div className={classes.practiceHero}>
                  <div className={classes.practiceHeroBg} />
                  <span className={classes.practiceHeroTitle}>
                    {practice?.title}
                  </span>
                  {practice?.description ? (
                    <span className={classes.practiceHeroDescription}>
                      {practice.description}
                    </span>
                  ) : null}
                  {practice?.author ? (
                    <span className={classes.practiceHeroAuthor}>
                      {`Nguồn: ${practice.author}`}
                    </span>
                  ) : null}
                  <div className={classes.practiceHeroStats}>
                    <span className={classes.practiceHeroStatBadge}>
                      <MdHistory />
                      {`${practice?.numberOfPracticed || 0} đã làm`}
                    </span>
                    {practice?.chapters ? (
                      <span className={classes.practiceHeroStatBadge}>
                        <GoListUnordered />
                        {`${practice.chapters.length || 0} chương`}
                      </span>
                    ) : null}
                    <span className={classes.practiceHeroStatBadge}>
                      <GoTasklist />
                      {`${practice?.numberOfQuestion || 0} câu hỏi`}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div className={classes.practice}>
              <div className={classes.cardBody}>
              <h5 className={classes.cardSectionHeading}>
                Thiết lập phòng luyện tập
              </h5>
              <div className={classes.configContainer}>
                {practiceFetching ? (
                  <div className={classes.emptyWrapper} style={{ height: 120 }}>
                    <LoadingIcon />
                  </div>
                ) : practiceError ? (
                  <div className={classes.emptyWrapper} style={{ height: 120 }}>
                    <LoadingButton
                      onClick={handleRefreshPractice}
                      fetching={practiceFetching}
                    >
                      Tải lại
                    </LoadingButton>
                  </div>
                ) : (
                  <>
                    {practice?.chapters && practice.chapters.length ? (
                      <>
                        <div className={classes.configItem}>
                          <span className={classes.configLabel}>
                            Luyện tập theo chương
                          </span>
                          <ControlledSwitch
                            checked={groupByChapter}
                            onChange={(e) =>
                              handleChangeGroupByChapter(e.target.checked)
                            }
                          />
                        </div>
                        {groupByChapter ? (
                          <>
                            <div className={classes.chapterConfigItem}>
                              <span className={classes.cardSectionHeading}>
                                Chọn chương
                              </span>
                              <div className={classes.chapterListContainer}>
                                {practice.chapters.map((item) => (
                                  <div
                                    className={mergeClassNames(
                                      classes.chapter,
                                      item.idx === selectedChapter?.idx
                                        ? classes.borderPrimary
                                        : ""
                                    )}
                                    onClick={() => setSelectedChapter(item)}
                                    key={item.idx}
                                  >
                                    <span
                                      className={mergeClassNames(
                                        classes.chapterTitle,
                                        item.idx === selectedChapter?.idx
                                          ? classes.colorPrimary
                                          : ""
                                      )}
                                    >
                                      {item.title}
                                    </span>
                                    <span
                                      className={mergeClassNames(
                                        classes.number,
                                        item.idx === selectedChapter?.idx
                                          ? classes.colorPrimary
                                          : classes.colorGray
                                      )}
                                    >
                                      {item.numberOfQuestion || 0}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {selectedChapter?.idx !== null &&
                            selectedChapter?.idx !== undefined &&
                            practice.chapters[selectedChapter.idx]
                              .description ? (
                              <div className={classes.chapterDescription}>
                                <span
                                  className={classes.chapterDescriptionTitle}
                                >
                                  Nội dung chương:{" "}
                                </span>{" "}
                                <span>
                                  {
                                    practice.chapters[selectedChapter.idx]
                                      .description
                                  }
                                </span>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </>
                    ) : null}
                    {!groupByChapter ||
                    !practice?.chapters ||
                    !practice.chapters.length ? (
                      <>
                        <div className={classes.configItem}>
                          <span className={classes.configLabel}>
                            Chọn số lượng câu hỏi
                          </span>
                          <select
                            value={numberOfQuestionPerGroup}
                            onChange={(e) =>
                              handleChangeNumberOfQuestionPerGroup(
                                e.target.value
                              )
                            }
                          >
                            {[
                              {
                                value: 20,
                                label: "20",
                              },
                              {
                                value: 50,
                                label: "50",
                              },
                              {
                                value: 75,
                                label: "75",
                              },
                              {
                                value: 100,
                                label: "100",
                              },
                            ].map((item) => (
                              <option key={item.value} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {numberOfQuestionPerGroup !== "OFF" ? (
                          <div className={classes.questionRangeConfigItem}>
                            <span className={classes.cardSectionHeading}>
                              Chọn bộ câu hỏi
                            </span>
                            <div className={classes.questionRangeListContainer}>
                              {questionRangeList.map((item) => (
                                <div
                                  className={mergeClassNames(
                                    classes.questionRange,
                                    item.startAt ===
                                      selectedQuestionRange?.startAt
                                      ? classes.borderPrimary
                                      : ""
                                  )}
                                  onClick={() => setSelectedQuestionRange(item)}
                                  key={item.endAt}
                                >
                                  <span
                                    className={mergeClassNames(
                                      classes.questionRangeLabel,
                                      item.startAt ===
                                        selectedQuestionRange?.startAt
                                        ? classes.colorPrimary
                                        : ""
                                    )}
                                  >{`Câu ${item.startAt} - ${item.endAt}`}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </>
                )}
                <div className={classes.configItem}>
                  <span className={classes.configLabel}>Kiểu hiển thị</span>
                  <select
                    value={practiceType}
                    onChange={(e) =>
                      setPracticeType(e.target.value as PracticeType)
                    }
                  >
                    {[
                      {
                        value: "SEQUENCE",
                        label: "Trả lời tuần tự từng câu hỏi",
                      },
                      {
                        value: "FLAT",
                        label: "Hiển thị toàn bộ câu hỏi",
                      },
                    ].map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
                {practiceType === "SEQUENCE" ? (
                  <div className={classes.configItem}>
                    <span className={classes.configLabel}>
                      Thời gian đếm ngược câu hỏi
                    </span>
                    <select
                      value={maxQuestionTime}
                      onChange={(e) =>
                        setMaxQuestionTime(
                          e.target.value === "OFF"
                            ? e.target.value
                            : parseInt(e.target.value)
                        )
                      }
                    >
                      {[
                        {
                          value: 20,
                          label: "20s",
                        },
                        {
                          value: 30,
                          label: "30s",
                        },
                        {
                          value: 50,
                          label: "50s",
                        },
                        {
                          value: "OFF",
                          label: "Tắt",
                        },
                      ].map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {practiceType === "SEQUENCE" ? (
                  <div className={classes.configItem}>
                    <span className={classes.configLabel}>
                      Hiển thị đáp án sau câu hỏi
                    </span>
                    <ControlledSwitch
                      checked={showAnswerAfterQuestion}
                      onChange={(e) =>
                        setShowAnswerAfterQuestion(e.target.checked)
                      }
                    />
                  </div>
                ) : null}
                <div className={classes.configItem}>
                  <span className={classes.configLabel}>Xáo trộn câu hỏi</span>
                  <ControlledSwitch
                    checked={suffer}
                    onChange={(e) => setSuffer(e.target.checked)}
                  />
                </div>
                {practiceType === "SEQUENCE" ? (
                  <div className={classes.configItem}>
                    <span className={classes.configLabel}>
                      Hiển thị thông báo
                    </span>
                    <ControlledSwitch
                      checked={showNotification}
                      onChange={(e) => setShowNotification(e.target.checked)}
                    />
                  </div>
                ) : null}
                {practiceType === "SEQUENCE" ? (
                  <div className={classes.configItem}>
                    <span className={classes.configLabel}>
                      Thời gian tự động chuyển câu hỏi
                    </span>
                    <select
                      value={maxResultTime}
                      onChange={(e) =>
                        setMaxResultTime(
                          e.target.value === "OFF"
                            ? e.target.value
                            : parseInt(e.target.value)
                        )
                      }
                    >
                      {[
                        {
                          value: 10,
                          label: "10s",
                        },
                        {
                          value: 20,
                          label: "20s",
                        },
                        {
                          value: 30,
                          label: "30s",
                        },
                        {
                          value: 50,
                          label: "50s",
                        },
                        {
                          value: "OFF",
                          label: "Tắt",
                        },
                      ].map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>
              </div>
            </div>
            {user ? (
              <div className={classes.practice}>
                <div className={classes.cardBody}>
                  <h5 className={classes.cardSectionHeading}>Lịch sử luyện tập</h5>
                  <PracticeTimeList
                    practiceTimes={practiceTimes}
                    fetching={practiceTimeFetching}
                    error={practiceTimeError}
                    className={classes.practiceTimeContainer}
                    handleRefresh={handleRefreshPracticeTime}
                  />
                </div>
              </div>
            ) : null}
          </div>
          <div className={classes.actionContainer}>
            <LoadingButton
              fetching={previewFetching}
              onClick={handleQuestionsPreview}
              className={classes.previewButton}
            >
              Xem trước câu hỏi
            </LoadingButton>
            <LoadingButton
              fetching={initRoomFetching}
              onClick={handleStartControl}
              className={classes.startButton}
            >
              {user ? "Bắt đầu" : "Đăng nhập để bắt đầu"}
            </LoadingButton>
          </div>
          <Tags
            tags={[
              "Ôn thi PTIT",
              "Ôn thi trắc nghiệm",
              "PTIT Quizz",
              "Trắc nghiệm PTIT",
              "Hệ thống ôn thi trắc nghiệm",
              "Ngân hàng câu hỏi",
              "An toàn và bảo mật hệ thống thông tin",
              "Kinh tế chính trị Mác - Lênin",
              "Cơ sở dữ liệu",
              "Tư tưởng Hồ Chí Minh",
              "Phương pháp luận nghiên cứu khoa học",
              "An toàn Web và CSDL",
              "Mạng máy tính",
              "Pháp luật đại cương",
            ]}
            className={classes.tagContainer}
          />
        </div>
      </div>
      <QuestionPreviewModal />
    </div>
  );
}

export default PracticeReview;
